import { FC, useState, useEffect } from 'react';
import { useMutation } from '@apollo/client';
import {
  Alert,
  Box,
  Divider,
  Grid,
  Tooltip,
  Typography,
  Button,
  Modal,
  Card,
  CardContent,
  CircularProgress,
  TextField
} from '@mui/material';
import { format } from 'date-fns';
import { getDollars } from '../../../../utils/currency';
import UserInvoices from '../UserInvoices';
import NavItem from 'src/components/NavItem';

import dayjs from 'dayjs';
import { APPLY_MEMBERSHIP_COUPON } from 'src/graphql/queries';
import { User } from 'src/models/user';
import { Membership } from 'src/models/membership';

import { UpcomingInvoice } from './UpcomingInvoice';
import { useApiMutation } from 'src/hooks/useApiMutation';
import UserMembershipContract from './UserMembershipContract';

type Props = {
  loading: boolean;
  user: User;
  displayMembership?: Membership;
  pro?: boolean;
  handleClickInvoiceRefund: (id: string, provider: string) => void;
  refundInvoiceLoading: boolean;
  userLoading: boolean;
  refreshUser: (email: string) => void;
};

const UserMembership: FC<Props> = ({
  user,
  loading,
  pro = false,
  displayMembership,
  handleClickInvoiceRefund,
  refundInvoiceLoading,
  userLoading,
  refreshUser
}) => {
  const currentMembership = pro ? user.proMembership : user.membership;
  const expiredMemberships = pro
    ? user.expiredProMemberships
    : user.expiredMemberships;

  if (!displayMembership) {
    displayMembership =
      currentMembership ??
      (expiredMemberships.length > 0
        ? expiredMemberships[expiredMemberships.length - 1]
        : null);
  }
  let expiredMessage = ``;
  if (
    displayMembership &&
    !currentMembership &&
    expiredMemberships.length > 0
  ) {
    expiredMessage = `Expired on ${format(
      displayMembership.endDate,
      'MMM dd, yyyy'
    )}`;
  }

  const contract =
    displayMembership?.subscription?.contracts.length > 0
      ? displayMembership.subscription.contracts[0]
      : null;

  const [
    executeCancelMembership,
    {
      loading: cancelMembershipLoading,
      data: cancelMembershipData,
      error: cancelMembershipError
    }
  ] = useApiMutation('billing', 'AdminCancelUserSubscription');

  const [
    executeCancelProMembership,
    {
      loading: cancelProMembershipLoading,
      data: cancelProMembershipData,
      error: cancelProMembershipError
    }
  ] = useApiMutation('billing', 'AdminCancelUserProSubscription');

  const [madeInitialQuery, setMadeInitialQuery] = useState(false);
  const [modalOpen, setModalOpen] = useState(false);

  const handleOpenModal = () => setModalOpen(true);
  const handleCloseModal = () => setModalOpen(false);

  //# Cancel membership
  const handleCancelMembershipActionClicked = () => {
    setMadeInitialQuery(false);
    handleOpenModal();
  };
  const handleCancelMembershipConfirm = async (
    subscriptionId: string,
    provider: string,
    metadata: []
  ) => {
    setMadeInitialQuery(true);
    if (pro) {
      executeCancelProMembership({
        variables: {
          subscriptionId: subscriptionId,
          provider: provider,
          metadata: metadata
        }
      });
    } else {
      executeCancelMembership({
        variables: {
          subscriptionId: subscriptionId,
          provider: provider,
          metadata: metadata
        }
      });
    }
  };
  const handleCancelMembershipCancel = async () => {
    setModalOpen(false);
  };

  useEffect(() => {
    if (madeInitialQuery && (cancelMembershipData || cancelProMembershipData)) {
      setModalOpen(false);
      refreshUser(user.email);
    }
  }, [cancelMembershipData, cancelProMembershipData]);

  const cancelMembershipConfirmModal = () => (
    <Modal
      open={modalOpen}
      onClose={handleCloseModal}
      aria-labelledby="modal-modal-title"
      aria-describedby="modal-modal-description"
      sx={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center'
      }}
    >
      <Box
        sx={{
          maxHeight: '100%',
          maxWidth: '500px',
          overflow: 'scroll'
        }}
      >
        <Card sx={{ mt: 3 }}>
          <CardContent>
            <Box sx={{ mb: 2 }}>
              <Typography sx={{ mb: 2 }} color="textPrimary" variant="h5">
                Cancel membership
              </Typography>
              <Typography color="textPrimary" variant="subtitle2">
                Are you sure you want to cancel membership for {user.firstName}{' '}
                {user.lastName}? Their subscription will NOT renew. However they
                will still have access to membership features until{' '}
                {dayjs(displayMembership.endDate).format('YYYY-MM-DD')}.
              </Typography>
              {madeInitialQuery && cancelMembershipError && (
                <Typography color="red" variant="subtitle2" textAlign="center">
                  {cancelMembershipError.message}
                </Typography>
              )}
              {madeInitialQuery && cancelProMembershipError && (
                <Typography color="red" variant="subtitle2" textAlign="center">
                  {cancelProMembershipError.message}
                </Typography>
              )}
            </Box>
            <Grid item md={12} xs={12} textAlign="center">
              {cancelMembershipLoading || cancelProMembershipLoading ? (
                <CircularProgress />
              ) : (
                <>
                  <Button
                    sx={{ m: 1 }}
                    color="secondary"
                    variant="contained"
                    onClick={handleCancelMembershipCancel}
                  >
                    No
                  </Button>
                  <Button
                    sx={{ m: 1 }}
                    color="primary"
                    variant="contained"
                    onClick={() => {
                      handleCancelMembershipConfirm(
                        displayMembership.subscription.subscriptionId,
                        displayMembership.subscription.provider,
                        []
                      );
                    }}
                  >
                    Yes
                  </Button>
                </>
              )}
            </Grid>
          </CardContent>
        </Card>
      </Box>
    </Modal>
  );

  const canCancelSubscription = () => {
    return (
      [
        'active',
        'trialing',
        'SUBSCRIPTION_STATE_ACTIVE',
        'SUBSCRIPTION_STATE_IN_GRACE_PERIOD',
        'SUBSCRIPTION_STATE_ON_HOLD',
        'SUBSCRIPTION_STATE_PAUSED'
      ].includes(displayMembership.subscription.status) &&
      !displayMembership.subscription.cancelAtPeriodEnd
    );
  };

  //# COUPON
  const [couponInputValue, setCouponInputValue] = useState('');

  const handleCouponInputChange = (event) => {
    if (applyCouponError) {
      applyCouponError.message = '';
    }
    setCouponInputValue(event.target.value);
  };

  const handleApplyCouponClicked = () => {
    setMadeInitialQuery(true);
    executeApplyCoupon({
      variables: {
        subscriptionId: displayMembership.subscription.subscriptionId,
        provider: displayMembership.subscription.provider,
        code: couponInputValue
      }
    }).then(() => {
      refreshUser(user.email);
    });
  };
  const [
    executeApplyCoupon,
    {
      loading: applyCouponLoading,
      data: applyCouponData,
      error: applyCouponError
    }
  ] = useMutation(APPLY_MEMBERSHIP_COUPON);

  const [
    executeClearCoupon,
    { loading: clearCouponLoading, error: clearCouponError }
  ] = useApiMutation('billing', 'AdminSubscriptionClearCoupon');

  const handleClearCouponClicked = async () => {
    try {
      await executeClearCoupon({
        variables: {
          provider: displayMembership.subscription.provider,
          subscriptionId: displayMembership.subscription.subscriptionId
        }
      });
      refreshUser(user.email);
    } catch (error) {
      console.error('Error clearing coupon:', error);
    }
  };

  const acceptedPlanMemberAccess = !pro
    ? (user.membershipPlanMemberAccess ?? []).find(
        (m) => m.status === 'accepted'
      )
    : null;
  const invitedPlanMemberAccessEntries = !pro
    ? (user.membershipPlanMemberAccess ?? []).filter(
        (m) => m.status === 'invited'
      )
    : [];
  return (
    <>
      {!loading && displayMembership && cancelMembershipConfirmModal()}
      {!loading && acceptedPlanMemberAccess && (
        <Box sx={{ marginTop: '1rem', marginBottom: '1rem' }}>
          <Card
            variant="outlined"
            sx={{
              borderRadius: 3,
              marginTop: '1rem',
              marginBottom: '1rem',
              padding: '1rem'
            }}
          >
            <CardContent>
              <Grid container spacing={3} key={acceptedPlanMemberAccess.id}>
                <Grid item xs={12}>
                  <Typography color="textPrimary" variant="h6">
                    Plan Member of{' '}
                    {acceptedPlanMemberAccess.membershipShortName} Membership
                  </Typography>
                </Grid>
                <Grid item xs={12}>
                  <Typography color="textPrimary" variant="body2">
                    This user is a plan member of{' '}
                    {acceptedPlanMemberAccess.ownerName}'s (
                    {acceptedPlanMemberAccess.ownerEmail}){' '}
                    {acceptedPlanMemberAccess.membershipShortName} plan
                  </Typography>
                </Grid>
                <Grid item md={3} xs={12}>
                  <Typography color="textPrimary" variant="subtitle2">
                    Invited At
                  </Typography>
                  <Typography color="textSecondary" variant="body2">
                    {format(
                      new Date(acceptedPlanMemberAccess.invitedAt.formatted),
                      'MMM dd, yyyy'
                    )}
                  </Typography>
                </Grid>
                {acceptedPlanMemberAccess.respondedAt && (
                  <Grid item md={3} xs={12}>
                    <Typography color="textPrimary" variant="subtitle2">
                      Responded At
                    </Typography>
                    <Typography color="textSecondary" variant="body2">
                      {format(
                        new Date(
                          acceptedPlanMemberAccess.respondedAt.formatted
                        ),
                        'MMM dd, yyyy'
                      )}
                    </Typography>
                  </Grid>
                )}
                <Grid item xs={12}>
                  <Typography color="textSecondary" variant="body2">
                    For more information about this membership see{' '}
                    {acceptedPlanMemberAccess.ownerEmail} user details.
                  </Typography>
                </Grid>
              </Grid>
            </CardContent>
          </Card>
          <Divider />
        </Box>
      )}
      {!loading && invitedPlanMemberAccessEntries.length > 0 && (
        <>
          <Box sx={{ marginTop: '1rem', marginBottom: '1rem' }}>
            <Card
              variant="outlined"
              sx={{
                borderRadius: 3,
                marginTop: '1rem',
                marginBottom: '1rem',
                padding: '1rem'
              }}
            >
              <CardContent>
                <Grid item xs={12}>
                  <Typography color="textPrimary" variant="h6">
                    Plan Member Invites
                  </Typography>
                  <Box sx={{ marginLeft: '1rem', marginTop: '0.5rem' }}>
                    <ul>
                      {invitedPlanMemberAccessEntries.map((m) => (
                        <li>
                          <Typography color="textSecondary" variant="body2">
                            {m.ownerName}
                            's ({m.ownerEmail}) sent an invitation to join their{' '}
                            {m.membershipShortName} Membership on{' '}
                            {format(
                              new Date(m.invitedAt.formatted),
                              'MMM dd, yyyy'
                            )}
                          </Typography>
                        </li>
                      ))}
                    </ul>
                  </Box>
                </Grid>
              </CardContent>
            </Card>
          </Box>
          <Divider />
        </>
      )}
      {!loading && displayMembership ? (
        <Box sx={{ marginTop: '1rem' }}>
          {(acceptedPlanMemberAccess ||
            invitedPlanMemberAccessEntries.length > 0) && (
            <Typography
              color="textPrimary"
              variant="h6"
              sx={{ marginBottom: '2rem' }}
            >
              {displayMembership.shortName} Membership
            </Typography>
          )}
          <Grid container spacing={3}>
            <Grid item md={3} xs={12}>
              <Typography color="textPrimary" variant="subtitle2">
                Membership Status
              </Typography>
              <Typography color="textSecondary" variant="body2">
                {displayMembership.status}
              </Typography>
            </Grid>

            <Grid item md={3} xs={12}>
              <Typography color="textPrimary" variant="subtitle2">
                Date Started
              </Typography>
              <Typography color="textSecondary" variant="body2">
                {format(displayMembership.startDate, 'MMM dd, yyyy')}{' '}
                {displayMembership.subscription.isTrialPendingHeadsetRegistration()
                  ? 'Trial will begin when the customer connects a headset.'
                  : ''}
              </Typography>
            </Grid>

            {displayMembership.subscription && (
              <>
                <Grid item md={3} xs={12}>
                  <Typography color="textPrimary" variant="subtitle2">
                    Provider
                  </Typography>
                  <Typography color="textSecondary" variant="body2">
                    {displayMembership.subscription.provider}
                  </Typography>
                </Grid>
                <Grid item md={3} xs={12}>
                  <Typography color="textPrimary" variant="subtitle2">
                    Subscription Status
                  </Typography>
                  <Typography color="textSecondary" variant="body2">
                    {displayMembership.subscription.status}
                  </Typography>
                </Grid>

                <Grid item md={3} xs={12}>
                  <Typography color="textPrimary" variant="subtitle2">
                    Period
                  </Typography>
                  <Typography color="textSecondary" variant="body2">
                    {displayMembership.subscription.period}
                  </Typography>
                </Grid>

                <Grid item md={3} xs={12}>
                  <Typography color="textPrimary" variant="subtitle2">
                    SubscriptionId
                  </Typography>
                  <Box sx={{ display: 'flex' }}>
                    <Tooltip
                      title={displayMembership.subscription.subscriptionId}
                      placement="top-start"
                    >
                      <Typography
                        color="textSecondary"
                        variant="body2"
                        style={{
                          overflow: 'hidden',
                          maxWidth: '250px',
                          whiteSpace: 'nowrap',
                          textOverflow: 'ellipsis'
                        }}
                      >
                        {displayMembership.subscription.subscriptionId}
                      </Typography>
                    </Tooltip>
                  </Box>
                </Grid>
                {displayMembership.subscription?.orderId && (
                  <Grid item md={3} xs={12}>
                    <Typography color="textPrimary" variant="subtitle2">
                      OrderId
                    </Typography>
                    <Typography color="textSecondary" variant="body2">
                      {displayMembership.subscription.orderId}
                    </Typography>
                  </Grid>
                )}

                <Grid item md={3} xs={12}>
                  <Typography color="textPrimary" variant="subtitle2">
                    Active Coupon
                  </Typography>
                  {displayMembership.subscription.couponCode ? (
                    <Typography color="textSecondary" variant="body2">
                      {displayMembership.subscription.couponCode} (
                      {displayMembership.subscription.couponNumberOfDays > 0
                        ? `${displayMembership.subscription.couponNumberOfDays} days`
                        : ''}
                      {displayMembership.subscription.discountAmount !== 0
                        ? `${getDollars(
                            displayMembership.subscription.discountAmount ?? 0
                          )} discount`
                        : ''}
                      )
                      <Button
                        size="small"
                        variant="text"
                        onClick={handleClearCouponClicked}
                        disabled={clearCouponLoading}
                      >
                        {clearCouponLoading ? '...' : 'Clear'}
                      </Button>
                    </Typography>
                  ) : (
                    <Typography color="textSecondary" variant="body2">
                      N/A
                    </Typography>
                  )}
                </Grid>
                <Grid item md={3} xs={12}>
                  <Typography color="textPrimary" variant="subtitle2">
                    Created with Coupon
                  </Typography>
                  <Typography color="textSecondary" variant="body2">
                    {displayMembership.subscription.couponAtSubscriptionCreation
                      ? `${
                          displayMembership.subscription
                            .couponAtSubscriptionCreation.couponCode
                        } (${
                          displayMembership.subscription
                            .couponAtSubscriptionCreation.couponNumberOfDays > 0
                            ? `${displayMembership.subscription.couponAtSubscriptionCreation.couponNumberOfDays} days`
                            : ''
                        }${
                          displayMembership.subscription
                            .couponAtSubscriptionCreation.discountAmount !== 0
                            ? `${getDollars(
                                displayMembership.subscription
                                  .couponAtSubscriptionCreation
                                  .discountAmount ?? 0
                              )} discount`
                            : ''
                        })`
                      : 'N/A'}
                  </Typography>
                </Grid>

                {displayMembership.shortName && (
                  <Grid item md={3} xs={12}>
                    <Typography color="textPrimary" variant="subtitle2">
                      Plan Type
                    </Typography>
                    <Typography color="textSecondary" variant="body2">
                      {displayMembership.shortName}
                    </Typography>
                  </Grid>
                )}

                {displayMembership.canShareAccess && (
                  <>
                    <Grid item md={3} xs={12}>
                      <Typography color="textPrimary" variant="subtitle2">
                        Plan Members Max
                      </Typography>
                      <Typography color="textSecondary" variant="body2">
                        {displayMembership.planMembersMax ?? 'N/A'}
                      </Typography>
                    </Grid>
                    <Grid item md={3} xs={12}>
                      <Typography color="textPrimary" variant="subtitle2">
                        Plan Members Active
                      </Typography>
                      <Typography color="textSecondary" variant="body2">
                        {displayMembership.planMembersActive ?? 0}
                      </Typography>
                    </Grid>
                    <Grid item md={3} xs={12}>
                      <Typography color="textPrimary" variant="subtitle2">
                        Plan Members Active or Invited
                      </Typography>
                      <Typography color="textSecondary" variant="body2">
                        {displayMembership.planMembersActiveOrInvited ?? 0}
                      </Typography>
                    </Grid>
                    {
                      <Grid item md={12} xs={12}>
                        <Typography color="textPrimary" variant="subtitle2">
                          Plan Members
                        </Typography>
                        <Typography
                          key={user.id}
                          color="textSecondary"
                          variant="body2"
                        >
                          {user.email} (owner)
                        </Typography>
                        {(displayMembership.planMemberAccess ?? [])
                          .filter(
                            (m) =>
                              m.status === 'accepted' || m.status === 'invited'
                          )
                          .map((m) => (
                            <Typography
                              key={m.id}
                              color="textSecondary"
                              variant="body2"
                            >
                              {m.email} ({m.status})
                            </Typography>
                          ))}
                      </Grid>
                    }
                  </>
                )}

                {expiredMessage && (
                  <Grid item md={12} xs={12}>
                    <Alert sx={{ mb: 1 }} severity="info">
                      {expiredMessage}
                    </Alert>
                  </Grid>
                )}
              </>
            )}
          </Grid>
          <UserMembershipContract
            contract={contract}
            membership={displayMembership}
            isExpired={displayMembership.subscription.isCanceled()}
          />
          <Divider sx={{ marginTop: '2rem', marginBottom: '10px' }} />
          {!expiredMessage && (
            <UpcomingInvoice membership={displayMembership} />
          )}
          {displayMembership.subscription.invoices?.length > 0 && (
            <Box sx={{ paddingTop: '10px' }}>
              <NavItem
                depth={0}
                title="Show Past Invoices"
                collapse={true}
                color="text"
              >
                <Box sx={{ paddingLeft: '0px' }}>
                  <Typography variant="h6" component="h2" sx={{ py: 2 }}>
                    Past Invoices
                  </Typography>
                  <UserInvoices
                    isShown={true}
                    invoices={displayMembership.subscription.invoices}
                    handleClickInvoiceRefund={handleClickInvoiceRefund}
                    refundInvoiceLoading={refundInvoiceLoading}
                    userLoading={userLoading}
                  />
                </Box>
              </NavItem>
            </Box>
          )}
          {displayMembership.status === 'active' && (
            <>
              <Divider sx={{ margin: '1rem 0' }} />
              <Grid container spacing={3}>
                <Grid item xs={6}>
                  <Box display="flex" alignItems="center" justifyContent="left">
                    <Typography color="textSecondary" variant="body2" my={1}>
                      Stop automatic renewal of membership?
                    </Typography>
                    <Button
                      sx={{ marginLeft: 2 }}
                      color="primary"
                      variant="contained"
                      onClick={handleCancelMembershipActionClicked}
                      disabled={
                        !canCancelSubscription() ||
                        !displayMembership.subscription.provider ||
                        displayMembership.subscription.provider === 'apple'
                      }
                    >
                      Cancel Membership
                    </Button>
                    {!displayMembership.subscription.provider ||
                      (displayMembership.subscription.provider === 'apple' && (
                        <>
                          <Typography
                            color="textSecondary"
                            variant="body2"
                            my={1}
                            sx={{ marginLeft: 2 }}
                          >
                            Unavailable for Apple memberships
                          </Typography>
                        </>
                      ))}
                  </Box>
                </Grid>
                {!displayMembership.isProfesssional() && (
                  <Grid item xs={6}>
                    <Box
                      display="flex"
                      alignItems="center"
                      justifyContent="left"
                    >
                      <Typography color="textSecondary" variant="body2" my={1}>
                        Redeem a coupon for the next billing cycle?
                      </Typography>
                      <TextField
                        error={Boolean(applyCouponError?.message)}
                        helperText={applyCouponError?.message}
                        label="Coupon"
                        margin="dense"
                        name="coupon"
                        size="small"
                        type="text"
                        value={couponInputValue}
                        onChange={handleCouponInputChange}
                        variant="outlined"
                        sx={{ mr: 2, minWidth: 300, marginLeft: 2 }}
                      />
                      <Button
                        color="primary"
                        variant="contained"
                        onClick={handleApplyCouponClicked}
                        disabled={
                          !(
                            couponInputValue.length > 0 &&
                            displayMembership.subscription.provider === 'stripe'
                          )
                        }
                      >
                        {applyCouponLoading &&
                        (applyCouponError?.message.length ?? 0 < 1) ? (
                          <CircularProgress color="success" size={40} />
                        ) : (
                          <Typography>Apply</Typography>
                        )}
                      </Button>
                      {!displayMembership.subscription.provider ||
                        (displayMembership.subscription.provider !==
                          'stripe' && (
                          <>
                            <Typography
                              color="textSecondary"
                              variant="body2"
                              my={1}
                              sx={{ marginLeft: 2 }}
                            >
                              Only available for Stripe memberships
                            </Typography>
                          </>
                        ))}
                    </Box>
                  </Grid>
                )}
              </Grid>
            </>
          )}
        </Box>
      ) : (
        <Alert sx={{ mt: 2 }} severity="info">
          This customer does not have a {pro ? 'professional' : 'personal'}{' '}
          membership!
        </Alert>
      )}
    </>
  );
};

export default UserMembership;
